import InteractiveAnnotation from "./interactiveAnnotation";
import SegmentChainArea from "./segmentChainArea";
import { segmentLengthPoints } from "../geometry";
import Line from "./line";
import Circle from "./circle";
import { measureText, setFontStyle } from "../canvasUtils";
import MeasurementText from "./measurementText";
export default class PDT extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);
    this.chain = new SegmentChainArea(position, options);

    this.pdtReady = false;
    this.spot = null;
    this.gld = null;

    this.activeText = false;

    this.measurementTexts = {
      area: new MeasurementText(),
      gld: new MeasurementText(),
      spot: new MeasurementText()
    } 
  }
  get isTextActive() {
    return !!this.activeText;
  }

  get areaText() {
    let scaled = this.chain.area * this.scale * this.scale;
    return scaled.toFixed(2) + " mm² Area";
  }

  get gldText() {
    let length = this.gld.length * this.scale;
    return length.toFixed(2) + " mm GLD";
  }

  get spotText() {
    let length = 2 * this.spot.radius * this.scale;
    return length.toFixed(2) + " mm Spot";
  }

  get areaTextWidth(){
    return measureText(this.areaText, this.font);
  }

  get gldTextWidth(){
    return measureText(this.gldText, this.font);
  }

  get spotTextWidth(){
    return measureText(this.spotText, this.font);
  }

  get textWidth() {
    return Math.max(this.areaTextWidth, this.spotTextWidth, this.spotTextWidth);
  }

  get textPosition() {
    let x = this.spot.centerX - this.textWidth / 2;
    let y = this.spot.centerY + this.spot.radius + 15;
    return { x, y };
  }

  draw(ctx) {
    this.chain.draw(ctx);

    if (this.pdtReady) {
      ctx.setLineDash([15, 15]);
      this.gld.draw(ctx);
      this.spot.draw(ctx);
      ctx.setLineDash([]);
      this.drawText(ctx);
      if(this.isTextActive) this.drawTextInteractionBoxes(ctx)    
    }
  }
  drawText(ctx) {
    setFontStyle(ctx, this);
    let pos = this.textPosition;
    this.measurementTexts.area.updateAndDraw(this.areaTextWidth, this.textSize, ctx, this.areaText, pos.x, pos.y);
    this.measurementTexts.gld.updateAndDraw(this.gldTextWidth, this.textSize, ctx, this.gldText, pos.x, pos.y + this.textSize);
    this.measurementTexts.spot.updateAndDraw(this.spotTextWidth, this.textSize, ctx, this.spotText, pos.x, pos.y + this.textSize * 2);
  }

  drawTextInteractionBoxes(ctx){  
    this.measurementTexts[this.activeText].drawTextInteractionBoxe(ctx);
  }

  updateTextLocation(x, y){
    this.measurementTexts[this.activeText].updateLocation(x, y)
  }

  isInBounds(x, y) {
    return this.chain.isInBounds(x, y)
      || (this.pdtReady && (
        this.isInBoundsText(x, y)
        || this.spot.isInBounds(x, y)
        || this.gld.isInBounds(x, y)
      ));
  }

  isInBoundsText(x, y) {
    return this.measurementTexts.area.isInBound(x, y)
    || this.measurementTexts.gld.isInBound(x, y)
    || this.measurementTexts.spot.isInBound(x, y);
  }

  mousedown(x, y) {
    if (this.measurementTexts.area.isInBound(x, y))
      this.activeText = "area";
    else if (this.measurementTexts.gld.isInBound(x, y))
      this.activeText = "gld";
    else if (this.measurementTexts.spot.isInBound(x, y))
      this.activeText = "spot";
    else{
      this.chain.mousedown(x, y);
      if (this.pdtReady) {
        this.spot.mousedown(x, y);
        this.gld.mousedown(x, y);
      }
    }   
    this.signalReady();
  }

  mousemove(x, y) {
    if (this.activeText) this.updateTextLocation(x, y)
    else{
      this.chain.mousemove(x, y);
      if (this.pdtReady) {
        this.spot.mousemove(x, y);
        this.gld.mousemove(x, y);
      }
    }   
    this.signalReady();
  }

  mouseup(x, y) {
    this.chain.mouseup(x, y);
    if (this.pdtReady) {
      this.spot.mouseup(x, y);
      this.gld.mouseup(x, y);
    } else {
      this.gld = this.generateGLD();
      this.spot = this.generateSpot();
      this.pdtReady = true;
    }
    this.signalReady();
  }

  generateGLD() {
    let points = this.chain.points;
    let maxDistance = 0;
    let p1 = null;
    let p2 = null;
    for (let i = 0; i < points.length - 1; i++) {
      let pi = points[i]
      for (let j = 1; j < points.length; j++) {
        let pj = points[j];
        let distance = segmentLengthPoints(pi, pj);
        if (distance > maxDistance) {
          maxDistance = distance;
          p1 = pi;
          p2 = pj;
        }
      }
    }
    let line = new Line(p1, this.color);
    line.setTail(p2.x, p2.y);
    line.fixedDimensions = true;
    return line;
  }

  generateSpot() {
    let oneMM = 1 / this.scale;
    let centerX = (this.gld.headX + this.gld.tailX) / 2;
    let centerY = (this.gld.headY + this.gld.tailY) / 2;
    let radius = (oneMM + this.gld.length) / 2;
    let circle = new Circle(centerX, centerY, radius, null, this.color);
    return circle;
  }

  deactivate(x, y) {
    this.activeText = null;
    this.chain.deactivate(x, y);
    if (this.pdtReady) {
      this.spot.deactivate(x, y);
      this.gld.deactivate(x, y);
    }
    this.signalReady();
  }

  setLineWidth(width) {
    this.chain.setLineWidth(width);
    this.gld.setLineWidth(width);
    this.spot.setLineWidth(width);
    this.signalReady();
  }


  updateColor(color) 
  {
    this.chain.color = color;
    this.signalReady();
  }
}